import { StaffWeightedWorkSearch } from './staff-weighted-work.model';
import { StaffBenchmarkPolicy } from './benchmark-policy.model';
import { StaffExclusionContext } from './staff-exclusions.model';
import { StaffReportingCalendar } from './staff-reporting-calendar.model';

export type AchievementStatus = 'below' | 'on' | 'above' | 'no_target' | 'unavailable';
export interface StaffAchievementSearch extends Omit<StaffWeightedWorkSearch, 'weighted_log_type_ids'> {
  action_ids?: number[] | null;
  access_level?: number | string;
}
export interface AchievementSummary {
  count: number;
  benchmark_percent: number | null;
  benchmark_status: AchievementStatus;
  eligible_weighted_minutes: number | null;
  target_weighted_minutes: number | null;
  variance_minutes: number | null;
  weighted_minutes: number | null;
  additional_count: number;
  additional_weighted_minutes: number;
  action_status_counts: Record<AchievementStatus, number>;
  daily_target_minutes: number;
  daily_coverage_percent: number | null;
  daily_coverage_status: AchievementStatus;
  comparison_incomplete: boolean;
}
export interface AchievementAction {
  log_type_id: number;
  name: string;
  kind: 'timed' | 'untimed';
  benchmark_ids: number[];
  configuration_error: string;
  unit_time_duration_ms: number | null;
  unit_per_day: number | null;
  count: number;
  expected_count: number | null;
  count_variance: number | null;
  minutes_per_action: number | null;
  weighted_ms: number | null;
  weighted_minutes: number | null;
  target_weighted_minutes: number | null;
  variance_minutes: number | null;
  achievement_percent: number | null;
  status: AchievementStatus;
}
export interface AchievementStaff extends AchievementSummary {
  user_id: number;
  name: string;
  timed_actions: AchievementAction[];
  untimed_actions: AchievementAction[];
  daily: Array<{ date: string; weighted_minutes?: number | null; eligible_weighted_minutes: number | null; target_weighted_minutes: number | null }>;
}
export interface StaffAchievementReport {
  version: 1;
  status: 'ready';
  context: StaffReportingCalendar & StaffExclusionContext & StaffBenchmarkPolicy & {
    generated_at: string;
    when_from: string;
    when_to: string;
    timezone: string;
    calendar_dates: string[];
    calendar_day_count: number;
    staff_count: number;
    daily_capacity_minutes: number;
    partial_dates: boolean;
    filters: StaffAchievementSearch;
    weight_policy: 'current';
    status_policy: 'exact';
  };
  team: AchievementSummary;
  staff: AchievementStaff[];
  configuration_errors: Array<{ log_type_id: number; name: string; benchmark_ids: number[]; message: string }>;
  available_actions: Array<{ log_type_id: number; name: string }>;
}
export type StaffAchievementSnapshot = StaffAchievementReport | { version: 1; status: 'unavailable'; reason: string };

export function achievementSnapshotDisplay(file: any, fallbackLabel: string): { label: string; report: StaffAchievementReport | null; message: string } {
  const value = file?.staff_achievement;
  return {
    label: file?.saved_file_name || fallbackLabel,
    report: value?.version === 1 && value?.status === 'ready' ? value : null,
    message: value?.reason || 'Staff achievement was not captured.'
  };
}
